import React from "react";

const buildPath = (points, width) => {
  if (!points || !Array.isArray(points) || points.length === 0) return "";
  const pts = points.length === 1
    ? [points[0], { x: width, y: points[0].y }]
    : points;
  let path = `M ${pts[0].x},${pts[0].y}`;
  for (let i = 1; i < pts.length; i += 1) {
    const prev = pts[i - 1];
    const current = pts[i];
    const midX = (prev.x + current.x) / 2;
    path += ` C ${midX},${prev.y} ${midX},${current.y} ${current.x},${current.y}`;
  }
  return path;
};

const EarningsLineCard = ({ title, series = [], categories = [], subtitle = "" }) => {
  const width = 480;
  const height = 200;

  // Safety checks: ensure series is array and has valid data
  if (!Array.isArray(series) || series.length === 0) {
    return (
      <div className="chart-card earnings-line-card">
        <div className="chart-card__title-row">
          <div>
            <span className="chart-card__title">{title || 'Chart'}</span>
            <span className="chart-card__subtitle">{subtitle}</span>
          </div>
        </div>
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--theme-text-secondary)' }}>
          No data available
        </div>
      </div>
    );
  }

  // Filter out series with invalid data
  const validSeries = series.filter(serie => 
    serie && 
    Array.isArray(serie.data) && 
    serie.data.length > 0
  );

  if (validSeries.length === 0) {
    return (
      <div className="chart-card earnings-line-card">
        <div className="chart-card__title-row">
          <div>
            <span className="chart-card__title">{title || 'Chart'}</span>
            <span className="chart-card__subtitle">{subtitle}</span>
          </div>
        </div>
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--theme-text-secondary)' }}>
          No data available
        </div>
      </div>
    );
  }

  const allValues = validSeries.flatMap((serie) => serie.data || []);
  const maxValue = Math.max(...allValues, 1);
  const minValue = Math.min(...allValues, 0);
  const range = maxValue - minValue || 1;

  // Ensure categories is array
  const validCategories = Array.isArray(categories) && categories.length > 0 
    ? categories 
    : validSeries[0]?.data?.map((_, i) => `Day ${i + 1}`) || [];

  const mapSeries = (serie) => {
    if (!serie || !Array.isArray(serie.data) || serie.data.length === 0) {
      return [];
    }
    const n = Math.max(validCategories.length, 1);
    const divisor = n > 1 ? n - 1 : 1;
    return serie.data.map((value, index) => {
      const x = (index / divisor) * width;
      const y = height - ((value - minValue) / range) * height;
      return { x, y };
    });
  };

  const mappedSeries = validSeries.map(mapSeries).filter(s => s.length > 0);
  const primarySeries = mappedSeries[0] || [];
  const secondarySeries = mappedSeries[1] || [];

  return (
    <div className="chart-card earnings-line-card">
      <div className="chart-card__title-row">
        <div>
          <span className="chart-card__title">{title}</span>
          <span className="chart-card__subtitle">{subtitle}</span>
        </div>
        {/* <button type="button" className="earnings-line-card__dropdown">
          Month
          <span className="caret" />
        </button> */}
      </div>

      <div className="earnings-line-card__chart">
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
          <defs>
            <linearGradient id="lineArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(var(--bs-primary-rgb), 0.35)" />
              <stop offset="100%" stopColor="rgba(var(--bs-primary-rgb), 0)" />
            </linearGradient>
          </defs>

          <path
            className="line-path secondary"
            d={buildPath(secondarySeries, width)}
            fill="none"
          />

          <path
            className="line-area"
            d={`${buildPath(primarySeries, width)} L ${width},${height} L 0,${height} Z`}
            fill="url(#lineArea)"
          />
          <path
            className="line-path primary"
            d={buildPath(primarySeries, width)}
            fill="none"
          />
        </svg>

        <div className="earnings-line-card__axis">
          {validCategories.map((label, index) => (
            <span key={label || index}>{label || `Day ${index + 1}`}</span>
          ))}
        </div>
      </div>

      <div className="chart-card__legend">
        {validSeries.map((serie, index) => (
          <div className="chart-card__legend-item" key={serie.name || serie.slug || index}>
            <span className={`legend-dot legend-dot-${serie.slug || 'default'}`} />
            {serie.name || 'Series'}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EarningsLineCard;

